import { A11yModule } from '@angular/cdk/a11y'
import { OverlayModule } from '@angular/cdk/overlay'
import { CommonModule } from '@angular/common'
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { Observable, tap } from 'rxjs'
import { TypedContextDirective } from '../../directives'
import { ErrorStateComponent } from '../error-state'
import { LoaderComponent, LoaderSize } from '../loader'
import { MenuComponent, MenuItems } from '../menu'
import { PopoverDirective } from '../popover'
import { RemoteComponent } from '../remote'
import { ApiResult, RemoteData } from '../remote-data'

/**
 * A general purpose dropdown menu component that attaches to an anchor element.
 *
 * This component provides a flexible dropdown menu that can be positioned relative to an anchor element.
 * The dropdown menu supports various positioning strategies and automatically handles focus management
 * and accessibility features.
 *
 * Provide menuItems for this component to work like a regular menu.
 * Provide menuItemsRemote for this component to display loading and error states.
 * The request is only triggered once and not reloaded when the menu is closed and reopened.
 *
 * @example
 * ```html
 * <ftr-dropdown-menu [menuItems]="items">
 *   <button>Click me</button>
 * </ftr-dropdown-menu>
 * ```
 *
 * @remarks
 * - The child element should be a button for accessibility reasons
 * - No need to hook up click handlers on the button - this component automatically handles click events
 * - For a more strict overflow menu with a flat icon button look, see `ftr-overflow-menu`
 * - The dropdown can be controlled externally via the `controlledIsOpen` input
 * - Supports both MenuItem arrays and MenuItemGroup arrays
 *
 * @see {@link MenuComponent} for menu rendering
 * @see {@link MenuItem} for individual menu item structure
 * @see {@link MenuItemGroup} for grouped menu items
 */
@Component({
  selector: 'ftr-dropdown-menu',
  templateUrl: './dropdown-menu.component.html',
  styleUrls: ['./dropdown-menu.component.css'],
  imports: [
    MenuComponent,
    OverlayModule,
    CommonModule,
    A11yModule,
    ErrorStateComponent,
    LoaderComponent,
    RemoteComponent,
    TypedContextDirective,
    PopoverDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownMenuComponent implements AfterViewInit {
  /**
   * An ApiResult may be supplied if your menu requires async data. The ApiResult will be subscribed to each time the
   * menu is opened, allowed for re-requests where necessary.
   */
  readonly menuItems = input.required<MenuItems | ApiResult<MenuItems>>()
  /**
   * This controls the width of the loading and error states, as well as the max width of the menu itself.
   */
  readonly maxWidth = input<number>(20)
  /**
   * This controls the minimum width of the menu itself.
   */
  readonly minWidth = input<number>()

  readonly controlledIsOpen = input<boolean>()
  readonly errorMessage = input<string>('Failed to load. Please refresh the page or try again later.')
  /**
   * Allows you to set a google analytics data-track-group on the menu, as it renders in a portal outside of your components tree
   */
  readonly menuTrackGroup = input<string>()

  readonly openChanged = output<boolean>()

  private readonly popoverOpen = signal(false)
  readonly isOpen = this.popoverOpen.asReadonly()

  protected readonly menuItemsApiResult = computed(() => {
    const menuItems = this.menuItems()
    if (menuItems instanceof Observable) {
      return menuItems.pipe(
        tap(() => {
          setTimeout(() => {
            this.popover().recomputePosition()
            this.popover().focus()
          })
        }),
      )
    }
    return ApiResult.success(menuItems)
  })

  protected readonly maxWidthRem = computed(() => `${this.maxWidth()}rem`)

  protected readonly popover = viewChild.required(PopoverDirective)

  protected readonly loaderSize = LoaderSize.Small
  protected readonly NotAsked = RemoteData.notAsked()

  readonly #destroyRef = inject(DestroyRef)
  private readonly controlledIsOpen$ = toObservable(this.controlledIsOpen)

  ngAfterViewInit(): void {
    this.controlledIsOpen$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(controlledIsOpen => {
      if (controlledIsOpen === true) {
        this.popover().openOverlay()
      }
      if (controlledIsOpen === false) {
        this.popover().closeOverlay()
      }
    })
  }

  protected handleOpenChanged(open: boolean): void {
    this.openChanged.emit(open)
    this.popoverOpen.set(open)
  }
}
